// Décors atmosphériques de biome rendus sur canvas, par thème d'étage.
// Appelé chaque frame depuis IsometricMap. Reçoit ctx, dimensions du canvas, le temps now, et le themeId.

const rand = (seed) => {
  let a = seed | 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Pré-génère N particules pour un thème. Stable par seed.
const buildParticles = (themeId, count, W, H) => {
  const r = rand(themeId.length * 1000 + count);
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({
      x: r() * W,
      y: r() * H,
      size: 2 + r() * 3,
      speedX: (r() - 0.5) * 0.4,
      speedY: 0.2 + r() * 0.6,
      offset: r() * 1000,
      driftAmp: 10 + r() * 20,
    });
  }
  return out;
};

// Cache des particules par theme + dimensions
const cache = new Map();

const getParticles = (themeId, W, H, count) => {
  const key = `${themeId}-${count}-${Math.round(W / 50)}-${Math.round(H / 50)}`;
  if (!cache.has(key)) cache.set(key, buildParticles(themeId, count, W, H));
  return cache.get(key);
};

export const drawBiomeDecor = (ctx, W, H, now, themeId) => {
  switch (themeId) {
    case "forest":
      return drawForest(ctx, W, H, now);
    case "cavern":
      return drawCavern(ctx, W, H, now);
    case "ruins":
      return drawRuins(ctx, W, H, now);
    case "glacial":
      return drawGlacial(ctx, W, H, now);
    case "volcano":
      return drawVolcano(ctx, W, H, now);
    case "celestial":
      return drawCelestial(ctx, W, H, now);
    case "astral":
      return drawAstral(ctx, W, H, now);
    case "abyss":
    default:
      return drawAbyss(ctx, W, H, now);
  }
};

function drawForest(ctx, W, H, now) {
  // Floating leaves
  const parts = getParticles("forest", W, H, 16);
  ctx.save();
  for (const p of parts) {
    const t = now / 1000;
    const y = (p.y + t * 30 * p.speedY) % H;
    const x = p.x + Math.sin(t + p.offset) * p.driftAmp;
    ctx.fillStyle = `rgba(74, 222, 128, ${0.4 + Math.sin(t + p.offset) * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(x, y, p.size + 1, p.size * 0.6, t + p.offset, 0, Math.PI * 2);
    ctx.fill();
  }
  // Green tint vignette
  const grad = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.3, W / 2, H / 2, Math.max(W, H) * 0.8);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(20, 83, 45, 0.35)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
}

function drawCavern(ctx, W, H, now) {
  // Glittering crystals — sparkles
  const parts = getParticles("cavern", W, H, 14);
  for (const p of parts) {
    const flicker = 0.4 + 0.6 * Math.abs(Math.sin(now / 400 + p.offset));
    ctx.fillStyle = `rgba(165, 180, 252, ${0.7 * flicker})`;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * 0.7, 0, Math.PI * 2);
    ctx.fill();
    // Cross sparkle
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.5 * flicker})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(p.x - p.size, p.y);
    ctx.lineTo(p.x + p.size, p.y);
    ctx.moveTo(p.x, p.y - p.size);
    ctx.lineTo(p.x, p.y + p.size);
    ctx.stroke();
  }
}

function drawRuins(ctx, W, H, now) {
  // Drifting dust motes
  const parts = getParticles("ruins", W, H, 18);
  for (const p of parts) {
    const t = now / 1000;
    const y = (p.y + t * 12 * p.speedY) % H;
    const x = p.x + Math.sin(t * 0.5 + p.offset) * p.driftAmp * 0.5;
    ctx.fillStyle = `rgba(217, 168, 116, ${0.25 + Math.sin(t + p.offset) * 0.15})`;
    ctx.beginPath();
    ctx.arc(x, y, p.size * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Warm sepia overlay
  ctx.fillStyle = "rgba(120, 85, 40, 0.08)";
  ctx.fillRect(0, 0, W, H);
}

function drawGlacial(ctx, W, H, now) {
  // Snowflakes falling
  const parts = getParticles("glacial", W, H, 28);
  for (const p of parts) {
    const t = now / 1000;
    const y = (p.y + t * 40 * p.speedY) % H;
    const x = p.x + Math.sin(t + p.offset) * p.driftAmp;
    ctx.fillStyle = `rgba(224, 242, 254, ${0.6 + Math.sin(t + p.offset) * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, p.size * 0.7, 0, Math.PI * 2);
    ctx.fill();
  }
  // Cold blue tint
  ctx.fillStyle = "rgba(56, 189, 248, 0.06)";
  ctx.fillRect(0, 0, W, H);
}

function drawVolcano(ctx, W, H, now) {
  // Embers rising from bottom
  const parts = getParticles("volcano", W, H, 22);
  for (const p of parts) {
    const t = now / 1000;
    const y = H - ((p.y + t * 50 * p.speedY) % H);
    const x = p.x + Math.sin(t * 2 + p.offset) * 6;
    const flicker = 0.5 + 0.5 * Math.sin(now / 80 + p.offset);
    ctx.fillStyle = `rgba(251, 146, 60, ${0.7 * flicker})`;
    ctx.beginPath();
    ctx.arc(x, y, p.size * 0.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(254, 240, 138, ${0.5 * flicker})`;
    ctx.beginPath();
    ctx.arc(x, y, p.size * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  // Red tint
  ctx.fillStyle = "rgba(190, 18, 60, 0.06)";
  ctx.fillRect(0, 0, W, H);
}

function drawCelestial(ctx, W, H, now) {
  // Soft gold sparkles
  const parts = getParticles("celestial", W, H, 24);
  for (const p of parts) {
    const flicker = 0.5 + 0.5 * Math.sin(now / 300 + p.offset);
    ctx.fillStyle = `rgba(254, 240, 138, ${0.5 * flicker})`;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
  // Gentle gold overlay
  ctx.fillStyle = "rgba(250, 204, 21, 0.04)";
  ctx.fillRect(0, 0, W, H);
}

function drawAstral(ctx, W, H, now) {
  // Spiraling stars
  const parts = getParticles("astral", W, H, 30);
  for (const p of parts) {
    const t = now / 1000;
    const angle = t * 0.3 + p.offset;
    const x = p.x + Math.cos(angle) * p.driftAmp;
    const y = p.y + Math.sin(angle) * p.driftAmp * 0.5;
    ctx.fillStyle = `rgba(216, 180, 254, ${0.55 + Math.sin(t + p.offset) * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, p.size * 0.6, 0, Math.PI * 2);
    ctx.fill();
  }
  // Purple radial
  const grad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.7);
  grad.addColorStop(0, "rgba(126, 34, 206, 0)");
  grad.addColorStop(1, "rgba(76, 29, 149, 0.3)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
}

function drawAbyss(ctx, W, H, now) {
  // Dark purple mist (brume violette sombre) — the "ombre" biome
  const layers = 3;
  for (let i = 0; i < layers; i++) {
    const t = now / 1000;
    const offset = i * 137;
    const grad = ctx.createRadialGradient(
      W / 2 + Math.sin(t * 0.3 + offset) * 80,
      H / 2 + Math.cos(t * 0.2 + offset) * 60,
      0,
      W / 2,
      H / 2,
      Math.max(W, H) * (0.4 + i * 0.2)
    );
    grad.addColorStop(0, `rgba(91, 33, 182, ${0.15 + 0.05 * Math.sin(t + offset)})`);
    grad.addColorStop(1, "rgba(15, 23, 42, 0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }
  // Floating purple wisps
  const parts = getParticles("abyss", W, H, 14);
  for (const p of parts) {
    const t = now / 1000;
    const x = p.x + Math.sin(t * 0.5 + p.offset) * p.driftAmp;
    const y = (p.y - t * 8 * p.speedY) % H;
    const fy = y < 0 ? y + H : y;
    const flicker = 0.4 + 0.4 * Math.sin(t * 2 + p.offset);
    ctx.fillStyle = `rgba(168, 85, 247, ${0.3 * flicker})`;
    ctx.beginPath();
    ctx.ellipse(x, fy, p.size * 3, p.size * 1.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  // Dark vignette
  const v = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.2, W / 2, H / 2, Math.max(W, H) * 0.8);
  v.addColorStop(0, "rgba(0,0,0,0)");
  v.addColorStop(1, "rgba(0, 0, 0, 0.5)");
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, W, H);
}
